// The Geocoder and the Places API name the same address parts differently
// (long_name vs longText); both are read through this shape so the rules below
// apply to an address whether it was searched for or clicked on the map.
export type AddressPart = { types: string[]; name: string };

// Google returns these as the "route" long_name when it has no actual street
// name on file for the location (e.g. parks, islands, private roads) — they
// are non-empty but not a usable address, so treat them as no match.
const PLACEHOLDER_ROUTE_NAMES = ['unnamed road', 'namnlös väg'];

export const findAddressPart = (parts: AddressPart[], type: string) =>
  parts.find(
    (part) =>
      part.types.includes(type) &&
      !(type === 'route' && PLACEHOLDER_ROUTE_NAMES.includes(part.name.toLowerCase()))
  )?.name ?? '';

// Rural addresses often have no street: Google returns the village as
// "locality" and the house number as "premise" (e.g. "Maglasäte 311"). This
// assumes the premise is a house number, which is what Swedish rural addresses
// give. A result with neither a route nor a premise, such as a bare postal
// town, has no street.
export const buildStreet = (parts: AddressPart[]) => {
  const premise = findAddressPart(parts, 'premise');
  const route = findAddressPart(parts, 'route');

  if (route) {
    return `${route} ${findAddressPart(parts, 'street_number') || premise}`.trim();
  }

  if (premise) {
    return `${findAddressPart(parts, 'locality')} ${premise}`.trim();
  }

  return '';
};

export const getAddressFromGeoCode = (results: google.maps.GeocoderResult[]) => {
  if (results.length === 0) {
    return undefined;
  }

  const result = results[0];
  const partsPerResult = results.map((candidate) =>
    candidate.address_components.map((component) => ({
      types: component.types,
      name: component.long_name,
    }))
  );

  // Reverse geocoding can return the requested component split across several
  // results at different granularities (e.g. the top "street_address" result
  // may omit postal_code/locality while a later "postal_code" result has it).
  const findAcrossResults = (getValue: (parts: AddressPart[]) => string) => {
    for (const parts of partsPerResult) {
      const value = getValue(parts);
      if (value) {
        return value;
      }
    }
    return '';
  };

  return {
    latitude: result.geometry.location.lat(),
    longitude: result.geometry.location.lng(),
    street: findAcrossResults(buildStreet),
    zipCode: findAcrossResults((parts) => findAddressPart(parts, 'postal_code')),
    city:
      findAcrossResults((parts) => findAddressPart(parts, 'postal_town')) ||
      findAcrossResults((parts) => findAddressPart(parts, 'locality')),
  };
};
